import { useState, useEffect, Suspense } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { securityService } from '@/lib/security'
import { getAuthFullUrl } from '@/lib/config'

function ForgotPasswordContent() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'error' | 'success' | 'info'>('info')
  const [step, setStep] = useState<'request' | 'sent' | 'error'>('request')
  const [retryCount, setRetryCount] = useState(0)
  const [canResend, setCanResend] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Check if user came from sign-in page
  useEffect(() => {
    const emailFromSignIn = searchParams?.get('email')
    if (emailFromSignIn) {
      setEmail(emailFromSignIn)
    }
  }, [searchParams])

  // Resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [resendTimer])

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    // Validate email
    if (!email) {
      setMessage('Please enter your email address.')
      setMessageType('error')
      setLoading(false)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address.')
      setMessageType('error')
      setLoading(false)
      return
    }


    try {
      // Check rate limiting
      const rateLimit = await securityService.checkLoginAttempts(email)
      if (!rateLimit.allowed) {
        setMessage(
          `Too many password reset attempts. Please wait 15 minutes before trying again.`
        )
        setMessageType('error')
        setStep('error')
        setLoading(false)
        return
      }

      // Send password reset email via backend API
      const getApiUrl = () => {
        const baseUrl = import.meta.env.VITE_API_URL;
        if (!baseUrl) {
          if (import.meta.env.DEV) return 'http://localhost:5001/api';
          console.error('VITE_API_URL is not set in production!');
          return '';
        }
        const cleanUrl = baseUrl.replace(/\/$/, '');
        return cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`;
      };
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          redirectUrl: getAuthFullUrl() + '/reset-password'
        })
      })

      const data = await response.json()
      const error = !response.ok ? { message: data.message } : null

      if (error) {
        console.error('Password reset error:', error)
        
        // Log security event
        await securityService.logSecurityEvent('password_reset', email, { action: 'password_reset_request', success: false, error: error.message })

        if (error.message.includes('User not found')) {
          // Don't reveal if user exists for security
          setMessage('If an account with this email exists, you will receive a password reset code.')
          setMessageType('info')
          setStep('sent')
        } else if (error.message.includes('Too many requests')) {
          setMessage('Too many password reset attempts. Please wait before trying again.')
          setMessageType('error')
          setStep('error')
        } else {
          setMessage('Unable to send password reset email. Please try again later.')
          setMessageType('error')
          setStep('error')
        }
      } else {
        // Log successful request
        await securityService.logSecurityEvent('password_reset', email, { action: 'password_reset_request', success: true })

        setMessage('Password reset code sent! Check your email and spam folder.')
        setMessageType('success')
        setStep('sent')
        setRetryCount(prev => prev + 1)
        setCanResend(false)
        setResendTimer(60) // 60 seconds cooldown
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      setMessage('An unexpected error occurred. Please try again.')
      setMessageType('error')
      setStep('error')
    } finally {
      setLoading(false)
    }
  }

  const handleResendEmail = async () => {
    if (!canResend || retryCount >= 3) return
    
    setLoading(true)
    setMessage('')
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const response = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          redirectUrl: getAuthFullUrl() + '/reset-password'
        })
      })

      const data = await response.json()
      const error = !response.ok ? { message: data.message } : null

      if (error) {
        setMessage('Failed to resend email. Please try again later.')
        setMessageType('error')
      } else {
        setMessage('Password reset code resent! Check your email.')
        setMessageType('success')
        setRetryCount(prev => prev + 1)
        setCanResend(false)
        setResendTimer(60)
      }
    } catch {
      setMessage('Failed to resend email. Please try again later.')
      setMessageType('error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            {step === 'request' && "Enter your email address and we'll send you a 6-digit code to reset your password."}
            {step === 'sent' && "We've sent you a password reset code."}
            {step === 'error' && "There was an issue sending the reset code."}
          </p>
        </div>

        {/* Request Form */}
        {step === 'request' && (
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email address"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm sm:text-base"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {/* Success State */}
        {step === 'sent' && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Check Your Email</h3>
              <p className="text-sm text-gray-600 mb-4">
                We&apos;ve sent a 6-digit reset code to <span className="font-medium text-gray-900">{email}</span>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">What&apos;s next?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Check your email inbox and spam folder</li>
                <li>• Go to the reset password page and enter the code</li>
                <li>• Enter your email and the code, then create a new password</li>
                <li>• The code expires in 1 hour</li>
              </ul>
            </div>

            <div className="flex flex-col space-y-2">
              <button
                onClick={() => navigate(`/auth/reset-password?email=${encodeURIComponent(email)}`)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
              >
                Enter reset code
              </button>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleResendEmail}
                disabled={!canResend || loading || retryCount >= 3}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? 'Sending...' : 
                 !canResend ? `Resend in ${resendTimer}s` :
                 retryCount >= 3 ? 'Max attempts reached' :
                 'Resend Email'}
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {step === 'error' && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to send reset code</h3>
              <p className="text-sm text-gray-600 mb-4">
                There was an issue sending the password reset code. Please try again.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('request')}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        )}

        {/* Message Display */}
        {message && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            messageType === 'error' 
              ? 'bg-red-100 text-red-700 border border-red-200' 
              : messageType === 'success'
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-blue-100 text-blue-700 border border-blue-200'
          }`}>
            {message}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Need help?</p>
            <div className="space-y-2 text-xs text-gray-500">
              <p>• Make sure you&apos;re using the email address you signed up with</p>
              <p>• Check your spam/junk folder</p>
              <p>• Contact support if you continue having issues</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ForgotPasswordPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    }>
      <ForgotPasswordContent />
    </Suspense>
  )
}

